import React, { useRef } from "react";
import {
    init as initEcharts,
    ECharts,
    EChartsOption,
    EChartsInitOpts,
} from "echarts";
import { useEffect } from 'react';

interface EchartsProps {
    options?: EChartsInitOpts,
    series?: EChartsOption['series'],
    run?: (
        chart: ECharts,
        containerDOM: HTMLDivElement
    ) => (() => void) | void,
    responsive?: (
        chart: ECharts,
        containerDOM: HTMLDivElement
    ) => {width: number, height: number}
}

const Echarts: React.FC<EchartsProps> = ({
    options,
    series,
    run,
    responsive
}) => {
    const domElementRef = useRef<HTMLDivElement>(null);
    const chartRef = useRef<ECharts>();
    const optionsRef = useRef(options);
    const responsiveRef = useRef(responsive);
    const runRef = useRef(run);

    useEffect(() => {
        if (!domElementRef.current) return;

        const options = optionsRef.current;
        const responsive = responsiveRef.current;
        const run = runRef.current;
        const containerElement = domElementRef.current;
        const chart = initEcharts(domElementRef.current, undefined, options);
        const runDestroy = run && run(chart, containerElement);
        let resizeObserver: ResizeObserver | null = null;

        if (responsive) {
            chart.resize(
                responsive(chart, containerElement)
            );

            setTimeout(() => {
                if (!chartRef.current || chartRef.current.isDisposed()) return;

                resizeObserver = responsive && new ResizeObserver((entries) => {
                    const containerElement = entries[0].target as HTMLDivElement;
                    chartRef.current?.resize(
                        responsive(chartRef.current, containerElement)
                    );
                });

                resizeObserver.observe(containerElement);
            }, 1000);
        }

        chartRef.current = chart;

        return () => {
            runDestroy && runDestroy();
            resizeObserver?.disconnect();
            chartRef.current?.dispose();
        };
    }, []);

    useEffect(() => {
        chartRef.current?.setOption({
            series
        })
    }, [series]);

    return <div ref={ domElementRef }/>;
};

export default Echarts;
